import React, { useEffect, useState, memo } from "react";

interface ResendTimerProps {
  storageKey: string;
  cooldownSeconds: number;
  onResend: () => Promise<void>;
  isResending: boolean;
  isDisabled?: boolean;
}

// Memoized component to prevent parent rerenders
const ResendTimer: React.FC<ResendTimerProps> = memo(({
  storageKey,
  cooldownSeconds,
  onResend,
  isResending,
  isDisabled = false,
}) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  // Initialize timer from localStorage on mount
  useEffect(() => {
    const storedTime = localStorage.getItem(storageKey);
    if (storedTime) {
      const expiryTime = parseInt(storedTime, 10);
      const now = Math.floor(Date.now() / 1000);
      const remaining = expiryTime - now;

      if (remaining > 0) {
        setTimeRemaining(remaining);
      } else {
        localStorage.removeItem(storageKey);
      }
    }
  }, [storageKey]);

  // Countdown timer
  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(storageKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, storageKey]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleResend = async () => {
    if (timeRemaining > 0 || isResending || isDisabled) return;

    await onResend();

    // Set timer after successful resend
    const expiryTime = Math.floor(Date.now() / 1000) + cooldownSeconds;
    localStorage.setItem(storageKey, expiryTime.toString());
    setTimeRemaining(cooldownSeconds);
  };

  if (timeRemaining > 0) {
    return (
      <p className="text-sm text-[#6F7782] font-sfLight">
        Resend code in{" "}
        <span className="text-[#FF6B35] font-sfMedium">
          {formatTime(timeRemaining)}
        </span>
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={handleResend}
      disabled={isResending || isDisabled}
      className="text-sm text-[#FF6B35] font-sfMedium hover:text-[#E55A25] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isResending ? "Resending..." : "Resend link"}
    </button>
  );
});

ResendTimer.displayName = "ResendTimer";

export default ResendTimer;
